import { Anchor, Modal, ScrollArea, Stack, Text, Title } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { type MouseEvent, useState } from "react";
import { useArtifactHandler } from "src/utils/artifactHandler";
import { formatDateTimeForCell } from "./TripAnalyzer/columns";
import type { TripAnalyzerSchema } from "./TripAnalyzer/types";

interface TripAnalyzerModalProps {
    rowData: TripAnalyzerSchema | null;
    onClose: () => void;
}

const TripAnalyzerModal = ({ rowData, onClose }: TripAnalyzerModalProps) => {
    const { handleArtifactClick, getFullUrl } = useArtifactHandler();
    const [isLoading, setIsLoading] = useState(false);

    const machine = rowData?._source?.machine;
    const summary = rowData?._source?.summary;
    const source = rowData?._source?.source;
    const timestamp = rowData?._source?.timestamp
        ? formatDateTimeForCell(new Date(rowData._source.timestamp))
        : undefined;

    const handleReportClick = async (e: MouseEvent<HTMLAnchorElement>) => {
        if (!source || isLoading) {
            e.preventDefault();
            return;
        }
        setIsLoading(true);
        try {
            await handleArtifactClick(e, source, [], undefined);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal opened={rowData !== null} onClose={onClose} title="Trip Analyzer" size="xl" py="sm">
            <Stack gap="sm">
                <Text size="sm" c="dimmed">
                    {[machine, timestamp].filter(Boolean).join("  •  ")}
                </Text>
                {source && (
                    <Anchor
                        href={getFullUrl(source)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleReportClick}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            width: "fit-content",
                        }}
                    >
                        <span>{isLoading ? "Opening..." : "View Full Report"}</span>
                        {!isLoading && <IconExternalLink size={14} />}
                    </Anchor>
                )}
                <Title order={5}>Summary</Title>
                <ScrollArea.Autosize mah="60vh">
                    <Text style={{ whiteSpace: "pre-wrap" }}>{summary || "N/A"}</Text>
                </ScrollArea.Autosize>
            </Stack>
        </Modal>
    );
};

export default TripAnalyzerModal;
